// Stripe configuration (without Stripe.js loading)
export const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

// Plan visibility configuration (control which plans are shown)
export const PLAN_VISIBILITY = {
  SHOW_FREE: import.meta.env.VITE_SHOW_FREE_PLAN === 'true',
  SHOW_PRO: import.meta.env.VITE_SHOW_PRO_PLAN === 'true',
  SHOW_BUSINESS: import.meta.env.VITE_SHOW_BUSINESS_PLAN === 'true',
  SHOW_ENTERPRISE: import.meta.env.VITE_SHOW_ENTERPRISE_PLAN === 'true'
};

// Price IDs for different tiers (you'll need to create these in your Stripe dashboard)
export const STRIPE_PRICE_IDS = {
  WALLET_CARD_MONTHLY: import.meta.env.VITE_STRIPE_PASS_ONLY_PRICE_ID || 'price_pass_only_monthly',
  STARTER_MONTHLY: import.meta.env.VITE_STRIPE_STARTER_PRICE_ID || 'price_starter_monthly',
  PRO_MONTHLY: import.meta.env.VITE_STRIPE_PRO_PRICE_ID || 'price_pro_monthly',
  BUSINESS_MONTHLY: import.meta.env.VITE_STRIPE_BUSINESS_PRICE_ID || 'price_business_monthly',
  ENTERPRISE_MONTHLY: import.meta.env.VITE_STRIPE_ENTERPRISE_PRICE_ID || 'price_enterprise_monthly'
};



// Export tier configurations with Stripe price IDs
export const EXPORT_TIERS_STRIPE = {
  walletCard: {
    name: 'walletCard',
    cost: 4.99,
    exportsPerMonth: 0, // No signature exports
    overageCost: 0,
    stripePriceId: STRIPE_PRICE_IDS.WALLET_CARD_MONTHLY,
    features: [
      '1 digital business card',
      'Apple Wallet + Google Wallet',
      'No SignatureForward branding',
      'Unlimited updates',
      'QR code sharing',
      'Basic analytics'
    ],
    productName: 'Wallet Card',
    description: 'Digital business card in your phone wallet. Perfect for networking and sharing your contact info instantly.',
    visible: true,
    intentOnly: ['wallet_only'] // Only show for these intents
  },
  starter: {
    name: 'starter',
    cost: 4.99,
    exportsPerMonth: 10,
    overageCost: 0,
    stripePriceId: STRIPE_PRICE_IDS.STARTER_MONTHLY,
    features: ['3 professional email signatures', 'Remove SignatureForward branding — your brand only', 'Custom profile photo', 'One-click Gmail install', '1 digital business card with QR code & share link', '1 wallet pass — your business card in Apple & Google Wallet', 'See who\'s engaging — simple view & click counter', 'CTA buttons, social icons & custom disclaimer', 'All professional templates'],
    productName: 'Starter Plan',
    description: 'Professional email signatures for individuals. ✓ 3 signatures ✓ Remove branding ✓ Custom profile photo ✓ 1 digital business card ✓ 1 wallet pass (Apple & Google Wallet) ✓ View & click counter ✓ CTA buttons, social icons ✓ All templates.',
    visible: true
  },
  pro: {
    name: 'pro',
    cost: 9.99,
    exportsPerMonth: -1, // -1 means unlimited
    overageCost: 0,
    stripePriceId: STRIPE_PRICE_IDS.PRO_MONTHLY,
    features: ['Up to 5 wallet passes — loyalty, coupon, event & generic', 'Reward repeat customers with points, tiers & rewards', 'Your emails distribute passes automatically', 'Reach up to 5,000 pass holders/month with campaigns', 'Shopify integration — auto-rewards on every purchase', 'Up to 10 signatures + one-click Gmail install', 'Banners, image gallery, video embeds & every add-on', 'Full analytics — device, link & source breakdowns', 'Connect loyalty events to your existing tools', 'Priority email support', 'Everything in Starter'],
    productName: 'Pro Plan',
    description: 'The plan used by every serious salesperson & founder. ✓ Up to 10 signatures ✓ Up to 5 wallet passes ✓ Email distribution ✓ Loyalty rewards ✓ Campaigns ✓ Full analytics ✓ Shopify integration ✓ Priority support.',
    visible: PLAN_VISIBILITY.SHOW_PRO
  },
  business: {
    name: 'business',
    cost: 19.99,
    exportsPerMonth: -1, // -1 means unlimited
    overageCost: 0,
    stripePriceId: STRIPE_PRICE_IDS.BUSINESS_MONTHLY,
    features: [
      'Up to 25 team members — no per-seat fees',
      'Unlimited email signatures & templates',
      'Managed members — admin creates, deploys signatures',
      'Policy & compliance controls with element locking',
      'Gmail signature sync for the whole team',
      'Apple + Google Wallet loyalty passes',
      'Loyalty programs with churn detection & re-engagement',
      'Full analytics — engagement scoring, heatmaps',
      'Campaigns to 50,000 recipients/month',
      'Shopify integration + webhooks + Zapier/Make',
      'Everything in Pro, plus team management'
    ],
    productName: 'Business Plan',
    description: 'Everything your team needs. One price. ✓ Up to 25 members, no per-seat fees ✓ Managed member pipeline ✓ Policy & compliance ✓ Gmail sync ✓ Wallet loyalty passes ✓ Full analytics ✓ Shopify ✓ Webhooks. Replace 4 tools with one — for $19.99/mo flat.',
    visible: PLAN_VISIBILITY.SHOW_BUSINESS
  },
  enterprise: {
    name: 'enterprise',
    cost: 99, // Custom pricing, this is placeholder
    exportsPerMonth: -1,
    overageCost: 0,
    stripePriceId: STRIPE_PRICE_IDS.ENTERPRISE_MONTHLY,
    features: ['10+ users (custom pricing)', 'Everything in Pro', 'SSO integration', 'API access', 'Dedicated account manager', 'SLA guarantees', 'Bulk user management', 'White-label options'],
    productName: 'Enterprise Plan',
    description: 'Custom enterprise solution with dedicated support and advanced features.',
    visible: PLAN_VISIBILITY.SHOW_ENTERPRISE
  }
};

// Helper function to get visible plans only
export const getVisiblePlans = () => {
  const visiblePlans: Partial<typeof EXPORT_TIERS_STRIPE> = {};
  Object.entries(EXPORT_TIERS_STRIPE).forEach(([key, plan]) => {
    if (plan.visible) {
      visiblePlans[key as keyof typeof EXPORT_TIERS_STRIPE] = plan;
    }
  });
  return visiblePlans;
};

// Helper function to check if a plan is visible
export const isPlanVisible = (planName: 'walletCard' | 'starter' | 'pro' | 'business' | 'enterprise') => {
  return EXPORT_TIERS_STRIPE[planName]?.visible || false;
};

// Helper function to check if a plan should be shown for a specific intent
export const isPlanVisibleForIntent = (planName: keyof typeof EXPORT_TIERS_STRIPE, intent: string | null) => {
  const plan = EXPORT_TIERS_STRIPE[planName];
  if (!plan?.visible) return false;
  
  // If plan has intentOnly restriction, check if current intent matches
  if ('intentOnly' in plan && plan.intentOnly) {
    return plan.intentOnly.includes(intent || '');
  }
  
  // Otherwise, plan is visible for all intents
  return true;
};

// Validate Stripe configuration
export const validateStripeConfig = () => {
  if (!STRIPE_PUBLIC_KEY) {
    console.error('❌ STRIPE_PUBLIC_KEY is not set in environment variables');
    return false;
  }
  
  if (!STRIPE_PUBLIC_KEY.startsWith('pk_')) {
    console.error('❌ Invalid Stripe public key format. Should start with "pk_"');
    return false;
  }
  
  console.log('✅ Stripe configuration is valid');
  return true;
}; 